/**
 * ContractRowActions - Menu de ações contextuais de um contrato
 *
 * Extraído da página CrmGeneratedContracts (sem alteração de comportamento)
 * para ser reaproveitado tanto na listagem em tabela quanto nos cards do
 * Kanban de Contratos. Respeita as permissões do usuário via usePermissions.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-12 v1.0.0 - Extração do componente interno da listagem
 */
import React, { useState } from 'react';
import {
  MoreHorizontal,
  Loader2,
  FileText,
  Trash2,
  Send,
  Download,
  RefreshCw,
  XCircle,
  FileCheck,
  RotateCcw,
  Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePermissions } from '@/hooks/usePermissions';
import { useContractActions } from '@/hooks/useContractActions';
import type { ContractStatus } from '@/types/contracts';
import { cn } from '@/lib/utils';

/** Subconjunto dos campos do contrato usados pelas ações */
export interface ContractActionsTarget {
  id: string;
  status: string;
  contract_number: number | null;
  sent_at: string | null;
  deal_id: string | null;
  pdf_storage_path: string | null;
  external_url: string | null;
}

interface ContractRowActionsProps {
  /** Contrato alvo das ações */
  contract: ContractActionsTarget;
  /** ID do tenant ativo */
  tenantId: string;
  /** Callback para abrir modal de detalhes do deal */
  onViewDeal: () => void;
  /** Callback após ação executada com sucesso (invalida queries) */
  onActionComplete: () => void;
  /** Classes extras do botão gatilho (ex.: manter sempre visível no card do Kanban) */
  triggerClassName?: string;
}

export function ContractRowActions({
  contract,
  tenantId,
  onViewDeal,
  onActionComplete,
  triggerClassName,
}: ContractRowActionsProps) {
  const { canEdit, canDelete, canView } = usePermissions();
  const [confirmAction, setConfirmAction] = useState<'delete' | 'cancel' | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const {
    generatePdf,
    sendForSignature,
    checkStatus,
    cancelContract,
    downloadPdf,
    retrySaveSignedPdf,
    resendSignature,
    deleteContract,
  } = useContractActions(contract.id, tenantId);

  const status = contract.status as ContractStatus;
  const hasPdf = !!contract.pdf_storage_path;
  const hasExternalUrl = !!contract.external_url;

  /** Executa ação com loading e callback de sucesso */
  const executeAction = async (mutation: { mutateAsync: (params?: any) => Promise<any> }, params?: any) => {
    setActionLoading(true);
    try {
      await mutation.mutateAsync(params);
      onActionComplete();
    } finally {
      setActionLoading(false);
    }
  };

  /** Handler para confirmar exclusão de rascunho */
  const handleConfirmDelete = async () => {
    await executeAction(deleteContract);
    setConfirmAction(null);
  };

  /** Handler para confirmar cancelamento */
  const handleConfirmCancel = async () => {
    await executeAction(cancelContract);
    setConfirmAction(null);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity',
              triggerClassName
            )}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <MoreHorizontal className="h-3.5 w-3.5" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">

          {/* --- DRAFT: Gerar PDF, Excluir --- */}
          {status === 'draft' && canEdit('crm_contracts') && (
            <DropdownMenuItem onClick={() => executeAction(generatePdf)}>
              <FileText className="h-4 w-4 mr-2 text-blue-500" />
              Gerar PDF
            </DropdownMenuItem>
          )}
          {status === 'draft' && canDelete('crm_contracts') && (
            <DropdownMenuItem
              onClick={() => setConfirmAction('delete')}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir Rascunho
            </DropdownMenuItem>
          )}

          {/* --- GENERATED: Enviar para Assinatura, Baixar PDF --- */}
          {status === 'generated' && canEdit('crm_contracts') && (
            <DropdownMenuItem onClick={() => executeAction(sendForSignature)}>
              <Send className="h-4 w-4 mr-2 text-amber-500" />
              Enviar para Assinatura
            </DropdownMenuItem>
          )}
          {status === 'generated' && hasPdf && canView('crm_contracts') && (
            <DropdownMenuItem onClick={() => executeAction(downloadPdf, {
              pdfStoragePath: contract.pdf_storage_path!,
              fileName: `contrato-${contract.contract_number || contract.id}.pdf`,
            })}>
              <Download className="h-4 w-4 mr-2 text-muted-foreground" />
              Baixar PDF
            </DropdownMenuItem>
          )}

          {/* --- SENT: Reenviar Link, Consultar Status, Cancelar --- */}
          {status === 'sent' && canEdit('crm_contracts') && (
            <>
              <DropdownMenuItem onClick={() => executeAction(resendSignature)}>
                <Send className="h-4 w-4 mr-2 text-amber-500" />
                Reenviar Link
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => executeAction(checkStatus)}>
                <RefreshCw className="h-4 w-4 mr-2 text-muted-foreground" />
                Consultar Status
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setConfirmAction('cancel')}
                className="text-destructive focus:text-destructive"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Cancelar Contrato
              </DropdownMenuItem>
            </>
          )}
          {status === 'sent' && hasPdf && canView('crm_contracts') && (
            <DropdownMenuItem onClick={() => executeAction(downloadPdf, {
              pdfStoragePath: contract.pdf_storage_path!,
              fileName: `contrato-${contract.contract_number || contract.id}.pdf`,
            })}>
              <Download className="h-4 w-4 mr-2 text-muted-foreground" />
              Baixar PDF Original
            </DropdownMenuItem>
          )}

          {/* --- SIGNED: Baixar PDF Original, Ver PDF Assinado --- */}
          {status === 'signed' && hasPdf && canView('crm_contracts') && (
            <DropdownMenuItem onClick={() => executeAction(downloadPdf, {
              pdfStoragePath: contract.pdf_storage_path!,
              fileName: `contrato-${contract.contract_number || contract.id}.pdf`,
            })}>
              <Download className="h-4 w-4 mr-2 text-muted-foreground" />
              Baixar PDF Original
            </DropdownMenuItem>
          )}
          {status === 'signed' && hasExternalUrl && canView('crm_contracts') && (
            <DropdownMenuItem onClick={() => window.open(contract.external_url!, '_blank')}>
              <FileCheck className="h-4 w-4 mr-2 text-green-500" />
              Ver PDF Assinado
            </DropdownMenuItem>
          )}
          {/* Signed sem external_url: tentar recuperar PDF assinado */}
          {status === 'signed' && !hasExternalUrl && canEdit('crm_contracts') && (
            <DropdownMenuItem onClick={() => executeAction(retrySaveSignedPdf)}>
              <RotateCcw className="h-4 w-4 mr-2 text-amber-500" />
              Recuperar PDF Assinado
            </DropdownMenuItem>
          )}

          {/* --- ERROR: Retry contextual --- */}
          {status === 'error' && canEdit('crm_contracts') && (
            <>
              {/* Se tem sent_at, o erro foi na assinatura → retry assinatura */}
              {contract.sent_at ? (
                <DropdownMenuItem onClick={() => executeAction(sendForSignature)}>
                  <RotateCcw className="h-4 w-4 mr-2 text-amber-500" />
                  Reenviar Assinatura
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => executeAction(generatePdf)}>
                  <RotateCcw className="h-4 w-4 mr-2 text-blue-500" />
                  Tentar Gerar PDF
                </DropdownMenuItem>
              )}
            </>
          )}

          {/* --- Separador + Ver Negócio (sempre visível) --- */}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={onViewDeal}
            disabled={!contract.deal_id}
          >
            <Eye className="h-4 w-4 mr-2 text-muted-foreground" />
            Ver Negócio
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* AlertDialog de confirmação para exclusão de rascunho */}
      <AlertDialog open={confirmAction === 'delete'} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Rascunho</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este contrato rascunho? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={actionLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AlertDialog de confirmação para cancelamento */}
      <AlertDialog open={confirmAction === 'cancel'} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Contrato</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja cancelar este contrato? O contrato será cancelado no provider de assinatura.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              disabled={actionLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Confirmar Cancelamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
